import React, { useState } from 'react';
import { Card, Form, Row, Col, ButtonGroup } from 'react-bootstrap';
import { Formik } from 'formik';
import * as yup from 'yup';
import { useHistory } from 'react-router-dom';
import { useMutation } from 'react-query';
import { toast } from 'react-toastify';
import { TIEUCHI_API, NHOMTIEUCHI_API } from '@app/utils/apiUrl';
import { Post, Get } from '@app/utils/httpProvider';
import { messageError, messageToast, status200, styleSelectDefault } from '../../../../../utils/define';
import { URI_INTERNAL } from '../../../../../utils/pathLocations';
import { Button } from '@components';
import { AsyncPaginate } from 'react-select-async-paginate';
import HorizontalFieldsRow from '@app/components/common/HorizontalFieldsRow';
import Editor from '@app/components/editor/Editor';

const schema = yup.object().shape({
    nhomTieuChiId: yup.object().required(messageError.emptyMessage).nullable(),
    tenTieuChi: yup.string().required(messageError.emptyMessage).max(255, 'Tên tiêu chí không được vượt quá 255 ký tự'),
    diemToiDa: yup.number().required(messageError.emptyMessage).min(0, 'Điểm tối đa không được nhỏ hơn 0'),
    thuTu: yup.number().required(messageError.emptyMessage).min(1, 'Thứ tự phải lớn hơn 0'),
});

const TieuChiAdd = () => {
    const history = useHistory();
    const [isLoading, setIsLoading] = useState(false);

    const addFunc = useMutation((value) =>
        Post(TIEUCHI_API.ADD_ITEM, null, value, false)
    );

    const handleCreate = async (values) => {
        const model = {
            nhomTieuChiId: values.nhomTieuChiId?.value,
            tenTieuChi: values.tenTieuChi,
            diemToiDa: values.diemToiDa,
            thuTu: values.thuTu,
            moTa: values.moTa
        };

        addFunc.mutate(model, {
            onSuccess: (res) => {
                if (res.status !== status200) {
                    toast.error(messageToast.messageToastCreateError);
                } else {
                    setIsLoading(true);
                    toast.success(messageToast.messageToastCreateSuccess);
                    history.goBack();
                }
            },
            onError: (res) => {
                toast.error(
                    res.message || 'Đã có lỗi xảy ra, vui lòng thử lại !'
                );
            }
        });
    };

    const loadOptionsNhomTieuChi = async (search, loadedOptions, { page }) => {
        const pageSize = 10;
        const res = await Get(NHOMTIEUCHI_API.GET_PAGING, null, {
            pageIndex: page,
            pageSize: pageSize,
            keyword: search
        });

        const items = res?.data?.$values ? res.data.$values : res?.data || [];

        return {
            options: items.map(item => ({
                value: item.id || item.Id,
                label: item.tenNhomTieuChi || item.TenNhomTieuChi
            })),
            hasMore: items.length >= pageSize,
            additional: {
                page: page + 1,
            },
        };
    };

    return (
        <Card>
            <Card.Header as="h5">Thêm mới tiêu chí</Card.Header>
            <Card.Body>
                <Formik
                    validationSchema={schema}
                    onSubmit={(values) => handleCreate(values)}
                    initialValues={{
                        nhomTieuChiId: null,
                        tenTieuChi: '',
                        diemToiDa: 0,
                        thuTu: 1,
                        moTa: ''
                    }}
                >
                    {({ handleSubmit, handleChange, values, errors, setFieldValue, setFieldTouched }) => (
                        <Form noValidate onSubmit={handleSubmit}>
                            <Form.Group as={Row} className="mb-3">
                                <Form.Label className="col-sm-2 col-form-label">
                                    Nhóm tiêu chí <span className="text-danger">*</span>
                                </Form.Label>
                                <Col sm="10">
                                    <AsyncPaginate
                                        value={values.nhomTieuChiId}
                                        loadOptions={loadOptionsNhomTieuChi}
                                        onChange={(value) => setFieldValue('nhomTieuChiId', value)}
                                        additional={{ page: 1 }}
                                        styles={styleSelectDefault}
                                        placeholder="Chọn nhóm tiêu chí"
                                        onBlur={() => setFieldTouched('nhomTieuChiId', true)}
                                        isClearable
                                        menuPortalTarget={document.body}
                                    />
                                    {errors.nhomTieuChiId && (
                                        <div className="text-danger mt-1" style={{ fontSize: '13px' }}>
                                            {errors.nhomTieuChiId}
                                        </div>
                                    )}
                                </Col>
                            </Form.Group>

                            <HorizontalFieldsRow
                                fields={[
                                    {
                                        label: 'Tên tiêu chí',
                                        name: 'tenTieuChi',
                                        value: values.tenTieuChi,
                                        onChange: handleChange,
                                        isInvalid: errors.tenTieuChi,
                                        error: errors.tenTieuChi
                                    },
                                    {
                                        label: 'Điểm tối đa',
                                        name: 'diemToiDa',
                                        type: 'number',
                                        value: values.diemToiDa,
                                        onChange: handleChange,
                                        isInvalid: errors.diemToiDa,
                                        error: errors.diemToiDa
                                    },
                                    {
                                        label: 'Thứ tự',
                                        name: 'thuTu',
                                        type: 'number',
                                        value: values.thuTu,
                                        onChange: handleChange,
                                        isInvalid: errors.thuTu,
                                        error: errors.thuTu
                                    },
                                ]}
                            />
                            <Form.Group as={Row} className="mb-3">
                                <Form.Label className="col-sm-2 col-form-label">
                                    Mô tả
                                </Form.Label>
                                <Col sm="10">
                                    <Col sm="10">
                                        <Editor
                                            name="moTa"
                                            value={values.moTa}
                                            onChange={(value) => {
                                                setFieldValue(
                                                    'moTa',
                                                    value
                                                );
                                            }}
                                        />
                                    </Col>
                                </Col>
                            </Form.Group>
                            <Form.Group as={Row} className="mb-3">
                                <Col md={{ span: 10, offset: 2 }}>
                                    <ButtonGroup className="mb-2">
                                        <Button type="submit" theme="primary" disabled={isLoading}>
                                            Thêm
                                        </Button>
                                        &nbsp;&nbsp;&nbsp;
                                        <Button onClick={() => history.push(URI_INTERNAL.TIEUCHI_PATH)} theme="secondary">
                                            Hủy
                                        </Button>
                                    </ButtonGroup>
                                </Col>
                            </Form.Group>
                        </Form>
                    )}
                </Formik>
                {isLoading && <div>Đang lưu...</div>}
            </Card.Body>
        </Card>
    );
};

export default TieuChiAdd;
